import { useState } from 'react';
import cx from 'classnames';
import { Icon } from '@iconify/react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components/Dialog';
import { getInitial } from '../../GlobalFunctions';
import tableStyles from '../Tables/Table.module.scss';
import { getChipColors, getMoodLabel } from '../WeeklyClassSnapshot/WeeklyClassSnapshot';
import type { GradeBand } from '../WeeklyClassSnapshot/WeeklyClassSnapshot';
import { getStudentWeek, getWeekDays } from '../moodboardWeek';
import type { SnapshotCheckIn } from '../moodboardSnapshot';
import styles from './DailyCheckInsTable.module.scss';

export interface MoodboardStudent {
  id: string;
  name: string;
}

interface DailyCheckInsTableProps {
  students: MoodboardStudent[];
  checkIns: SnapshotCheckIn[];
  weekStart: number;
  gradeBand: GradeBand;
  hideNames?: boolean;
  isLarge?: boolean;
  // Without it the table is display only
  onSelectStudent?: (id: string) => void;
}

const isWeekend = (dayIndex: number) => dayIndex > 4;

const formatTime = (createdAt: number) =>
  new Date(createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const MoodChip = ({ mood, gradeBand, count = 1 }: { mood: string; gradeBand: GradeBand; count?: number }) => (
  <span className={styles.chip} style={getChipColors(mood, gradeBand)}>
    {getMoodLabel(mood)}
    {count > 1 && <span className={styles.count}>{count}</span>}
  </span>
);

export const DayCell = ({
  checkIns,
  dayIndex,
  gradeBand,
  details,
}: {
  checkIns: SnapshotCheckIn[];
  dayIndex: number;
  gradeBand: GradeBand;
  details?: { date: Date; studentName: string };
}) => {
  const [isOpen, setOpen] = useState(false);
  const [latest] = checkIns;
  if (!latest) return <span className={styles.empty}>{isWeekend(dayIndex) ? '–' : 'No check-in'}</span>;

  const chip = <MoodChip mood={latest.mood} gradeBand={gradeBand} count={checkIns.length} />;
  const source = gradeBand === 'secondary' && <span className={styles.source}>{latest.sources.join(', ')}</span>;

  if (!details || checkIns.length === 1) {
    return (
      <>
        {chip}
        {source}
      </>
    );
  }

  return (
    // Portal clicks bubble through React, so keep them from opening the student row
    <span onClick={(event) => event.stopPropagation()}>
      <button
        type='button'
        className={styles.chipButton}
        aria-label={`${checkIns.length} check-ins, show details`}
        onClick={() => setOpen(true)}
      >
        {chip}
      </button>
      {source}
      <Dialog open={isOpen} onOpenChange={setOpen}>
        <DialogContent>
          <DialogTitle>
            {details.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </DialogTitle>
          <DialogDescription>
            {details.studentName.split(' ')[0]} checked in {checkIns.length} times. The most recent check-in shows in
            the table.
          </DialogDescription>
          <ul className={styles.dayList}>
            {checkIns.map((checkIn) => (
              <li key={checkIn.createdAt}>
                <span className={styles.time}>{formatTime(checkIn.createdAt)}</span>
                <MoodChip mood={checkIn.mood} gradeBand={gradeBand} />
                {gradeBand === 'secondary' && <span className={styles.source}>{checkIn.sources.join(', ')}</span>}
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </span>
  );
};

const DailyCheckInsTable = ({
  students,
  checkIns,
  weekStart,
  gradeBand,
  hideNames = false,
  isLarge = false,
  onSelectStudent,
}: DailyCheckInsTableProps) => {
  const days = getWeekDays(weekStart);

  return (
    <div className={styles.tableWrap}>
      <table className={cx(tableStyles.tableContainer, styles.table, { [styles.large]: isLarge })}>
        <thead>
          <tr>
            <th>Student</th>
            {days.map((date, i) => (
              <th key={i} className={cx({ [styles.weekend]: isWeekend(i) })}>
                {date.toLocaleDateString('en-US', { weekday: 'short' })}
                <small>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</small>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {students.map((student, rowIndex) => (
            <tr
              key={student.id}
              className={cx({ [styles.clickableRow]: onSelectStudent })}
              onClick={onSelectStudent && (() => onSelectStudent(student.id))}
            >
              <td>
                <div className={styles.student}>
                  <span className={styles.avatar} aria-hidden='true'>
                    {hideNames ? rowIndex + 1 : getInitial(student.name)}
                  </span>
                  {onSelectStudent ? (
                    <>
                      {/* Keyboard access; its click bubbles to the row handler */}
                      <button type='button' className={styles.rowTrigger}>
                        {student.name}
                      </button>
                      <Icon className={styles.rowChevron} icon='mdi:chevron-right' aria-hidden='true' />
                    </>
                  ) : (
                    <b>{hideNames ? `Student ${rowIndex + 1}` : student.name}</b>
                  )}
                </div>
              </td>
              {getStudentWeek(checkIns, student.id, weekStart).map((dayCheckIns, i) => (
                <td key={i} className={cx({ [styles.weekend]: isWeekend(i) })}>
                  <DayCell
                    checkIns={dayCheckIns}
                    dayIndex={i}
                    gradeBand={gradeBand}
                    details={onSelectStudent && { date: days[i], studentName: student.name }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DailyCheckInsTable;
